/* eslint-disable @typescript-eslint/no-explicit-any */

import {Grid, TextField, Typography} from "@material-ui/core";
import Avatar from "@material-ui/core/Avatar";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import AddToPhotosIcon from "@material-ui/icons/AddToPhotos";
import useTranslation from "next-translate/useTranslation";
import {useRouter} from "next/router";
import React from "react";
import {useForm, Controller} from "react-hook-form";
import * as Yup from "yup";

import {createSpace} from "@sentrei/common/services/spaces";

import Props from "@sentrei/common/types/components/SpaceForm";
import spaceFormType from "@sentrei/common/types/spaceFormType";
import {timestamp} from "@sentrei/common/utils/firebase";
import ImageUpload from "@sentrei/ui/components/ImageUpload";
import useSnackbar from "@sentrei/ui/hooks/useSnackbar";

import SpaceFormStyles from "./SpaceFormStyles";

const SpaceForm = ({profile, type, user}: Props): JSX.Element => {
  const classes = SpaceFormStyles();
  const {push} = useRouter();
  const {snackbar} = useSnackbar();
  const {t} = useTranslation();

  const SpaceFormSchema = Yup.object().shape({
    name: Yup.string().required(t("space:space.name.required")),
    description: Yup.string(),
    id: Yup.string(),
    photo: Yup.string(),
  });

  const {control, register, errors, handleSubmit, setValue, watch} = useForm({
    reValidateMode: "onBlur",
    validationSchema: SpaceFormSchema,
  });

  const photoValue = watch("photo");
  const handlePhoto = (url: string): void => setValue("photo", url);
  React.useEffect(() => {
    register({name: "photo"});
  }, [register]);

  const {back} = useRouter();

  const onSubmit = async (data: Record<string, any>): Promise<void> => {
    snackbar("info", t("space:space.common.creating"));
    switch (type) {
      case spaceFormType.create:
        try {
          await createSpace({
            name: data.name,
            description: null,
            photo: null,
            createdAt: timestamp,
            createdBy: profile,
            createdById: user.uid,
            memberCount: 0,
            updatedAt: timestamp,
            updatedBy: profile,
            updatedById: user.uid,
          }).then(id => {
            snackbar("success");
            push("/[id]", `/${id}`);
          });
        } catch (err) {
          snackbar("error", err.message);
        }
        break;
      default:
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          {type === spaceFormType.create && <AddToPhotosIcon />}
        </Avatar>
        <Typography component="h1" variant="h3">
          {t("space:spaceCreate.title")}
        </Typography>
        <Box pb={3} />
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                as={
                  <TextField
                    autoFocus
                    fullWidth
                    id="space-name"
                    label="Name"
                    margin="normal"
                    name="name"
                    required
                    variant="outlined"
                    error={!!errors.name}
                    inputRef={register}
                    helperText={errors.name ? errors.name.message : ""}
                    type="text"
                  />
                }
                name="name"
                control={control}
                defaultValue=""
              />
            </Grid>
            {type === spaceFormType.edit && (
              <Grid item xs={12}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item>
                    <Typography
                      variant="h5"
                      color="textSecondary"
                      display="inline"
                      gutterBottom
                    >
                      sentrei.com/
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Controller
                      as={
                        <TextField
                          id="space-id"
                          label="ID"
                          margin="normal"
                          name="id"
                          required
                          variant="outlined"
                          error={!!errors.id}
                          inputRef={register}
                          helperText={errors.id ? errors.id.message : ""}
                          type="text"
                          size="small"
                        />
                      }
                      name="id"
                      control={control}
                      defaultValue=""
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            {type === spaceFormType.edit && (
              <Grid item xs={12}>
                <Controller
                  as={
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      id="space-description"
                      label="Description"
                      margin="normal"
                      name="description"
                      required
                      variant="outlined"
                      error={!!errors.description}
                      inputRef={register}
                      helperText={
                        errors.description ? errors.description.message : ""
                      }
                      type="text"
                    />
                  }
                  name="description"
                  control={control}
                  defaultValue=""
                />
              </Grid>
            )}
            {type === spaceFormType.edit && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {t("space:space.cover")}
                </Typography>
                <ImageUpload
                  id="add-cover-img"
                  img={photoValue}
                  onSave={handlePhoto}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
              >
                {t("space:space.create")}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                type="reset"
                fullWidth
                variant="outlined"
                color="primary"
                onClick={back}
              >
                {t("space:space.cancel")}
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </Container>
  );
};

export default SpaceForm;
